import { chartTheme } from "@/lib/chartTheme";

interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color?: string }>;
  label?: string;
  labelFormatter?: (label: string) => string;
  valueFormatter?: (value: number, name: string) => string;
  unit?: string;
}

export function ChartTooltip({
  active,
  payload,
  label,
  labelFormatter,
  valueFormatter,
  unit,
}: ChartTooltipProps) {
  if (!active || !payload?.length) return null;

  const displayLabel = labelFormatter ? labelFormatter(label ?? "") : label;

  return (
    <div
      style={{
        backgroundColor: chartTheme.tooltipBg,
        border: `1px solid ${chartTheme.tooltipBorder}`,
        borderRadius: 0,
        padding: "8px 12px",
        fontFamily: chartTheme.fontFamily,
        fontSize: 11,
        color: "rgba(255,255,255,0.9)",
        boxShadow: "none",
        pointerEvents: "none",
      }}
    >
      {displayLabel && (
        <p style={{ color: "rgba(255,255,255,0.5)", marginBottom: 4, fontSize: 10 }}>
          {displayLabel}
        </p>
      )}
      {payload.map((entry, i) => {
        const raw = entry.value;
        const display = valueFormatter
          ? valueFormatter(raw, entry.name)
          : unit
          ? `${raw}${unit}`
          : typeof raw === "number"
          ? raw.toLocaleString()
          : raw;

        const color =
          entry.color ??
          (typeof raw === "number" && raw >= 0
            ? chartTheme.positiveColor
            : chartTheme.negativeColor);

        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 10 }}>{entry.name}</span>
            <span style={{ color, fontWeight: 600, marginLeft: "auto", paddingLeft: 16 }}>
              {display}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Single-value shorthand for simple charts */
export function PnlTooltip({
  active,
  payload,
  label,
  labelFormatter,
}: ChartTooltipProps) {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value ?? 0;
  const displayLabel = labelFormatter ? labelFormatter(label ?? "") : label;

  return (
    <div
      style={{
        backgroundColor: chartTheme.tooltipBg,
        border: `1px solid ${chartTheme.tooltipBorder}`,
        borderRadius: 0,
        padding: "8px 12px",
        fontFamily: chartTheme.fontFamily,
        fontSize: 11,
        color: "rgba(255,255,255,0.9)",
        boxShadow: "none",
        pointerEvents: "none",
      }}
    >
      {displayLabel && (
        <p style={{ color: "rgba(255,255,255,0.5)", marginBottom: 4, fontSize: 10 }}>
          {displayLabel}
        </p>
      )}
      <span
        style={{
          color: val >= 0 ? chartTheme.positiveColor : chartTheme.negativeColor,
          fontWeight: 600,
        }}
      >
        {val >= 0 ? "+" : ""}${val.toLocaleString()}
      </span>
    </div>
  );
}
